import { request } from './utils';
import {
    formatDate,
    formatUSDateToISO,
    loanServerSidePaginationSearchParams
} from './../utils/formatters';
import { downloadReport } from './../utils/downloadHelper';
import download from "downloadjs";
import { isSuccessfulResponse } from '../utils/responseHelpers';

// P4 loan hold statuses (was dpp/utils/constants.js LOAN_HOLD_STATUS).
const LOAN_HOLD_STATUS = {
    HOLD_FOR_FUNDING: "Hold for funding",
    HOLD_FOR_PAYMENT: "Hold for payment",
    RELEASE_FOR_FUNDING: "Release for funding",
    RELEASE_FOR_PAYMENT: "Release for payment",
    PENDING_RELEASE: "Pending release tomorrow",
};

// dollars -> integer cents (was dpp/utils/helpers.js toCents).
const toCents = (dollars) => Math.round(Number.parseFloat(dollars) * 100);

export default class LoanService {
    static async getAllVerbose(page, perPage, searchText, filterList) {
        const response = await request(`/api/loans${loanServerSidePaginationSearchParams(page, perPage, searchText, filterList)}`);
        return response.data;
    }

    static async getSingleLoanAPI(loanNumber) {
        const response = await request(`/api/loans/${loanNumber}`);

        return response.data;
    }

    // TODO MIGRATION: `/loans/:id/verbose` had no DPP route and no P4 equivalent.
    // Confirm if still used; P4 may need a `verbose` view on the loan show action.
    static async getSingleLoanVerbose(loanNumber) {
        const response = await request(`/api/loans/${loanNumber}`);

        return response.data;
    }

    static async getSingleLoanVisionCalculations(loanNumber) {
        const response = await request(`/api/loans/${loanNumber}/vision_calculator_stats`);
        return response.data;
    }

    static async sendEmail() {
        const response = await request('/api/emails/send_test_email', { method: 'POST' });

        return response.data;
    }

    static async getHoldsFromDailyProcessing(date) {
        const response = await request('/api/loans/held');

        return response.data;
    }

    static async getProcessingPaymentsFromDailyProcessing(date) {
        const formattedDate = formatUSDateToISO(date);
        const response = await request(`/api/payments/processing?date=${encodeURIComponent(formattedDate)}`);

        return response.data;
    }

    // DPP merged P4's funding_requests + funding_requests/summary into one payload.
    static async getFundingRequestsFromDailyProcessing(date) {
        const formattedDate = formatUSDateToISO(date);
        const [fundingRequestsResponse, summaryResponse] = await Promise.all([
            request('/api/funding_requests', { params: { date: formattedDate } }),
            request('/api/funding_requests/summary', { params: { date: formattedDate } }),
        ]);

        return {
            fundingRequestSummary: summaryResponse.data?.summary,
            fundingRequests: fundingRequestsResponse.data?.fundingRequests,
        };
    }

    static async getPaymentsFromDailyProcessing(date) {
        const formattedDate = formatUSDateToISO(date);
        const response = await request(`/api/payments?date=${encodeURIComponent(formattedDate)}`);

        return response.data;
    }

    static async getReturnsFromDailyProcessing(date) {
        const formattedDate = formatUSDateToISO(date);
        const response = await request(`/api/returns?date=${encodeURIComponent(formattedDate)}`);

        return response.data;
    }

    static async getNotificationsFromDailyProcessing(date) {
        const formattedDate = new Date(date).toISOString().slice(0, 10);
        const response = await request('/api/notifications', { params: { date: formattedDate } });

        return response.data;
    }

    static async getRulesFromDailyProcessing(date) {
        const formattedDate = formatDate(date);
        const response = await request(`/api/rules/failures/${encodeURIComponent(formattedDate)}`);

        return response.data;
    }

    static async getNlsAuditLogsFromDailyProcessing(date) {
        const formattedDate = formatUSDateToISO(date);
        const response = await request(`/api/nls/audits/${encodeURIComponent(formattedDate)}`);

        return response.data;
    }

    static async pullDelinquenciesComment(){
        await request('/api/delinquency_comments/pull', { method: 'POST' });
    }

    static async repullDataDump() {
        await request('/api/loans/data_dumps', { method: 'POST' });
    }

    static async downloadNachaFile(date) {
        await downloadReport(`/api/payments/nacha_files/wsfs?date=${encodeURIComponent(formatUSDateToISO(date))}`, 'Nacha');
    }

    static async downloadThreadNachaFile(date) {
        await downloadReport(`/api/payments/nacha_files/thread?date=${encodeURIComponent(formatUSDateToISO(date))}`, 'Thread Nacha');
    }

    static async downloadThreadNachaReturnsFile(date) {
        await downloadReport(`/api/returns/nacha_files/thread?date=${encodeURIComponent(formatUSDateToISO(date))}`, 'Thread Nacha');
    }

    static async downloadWsfsNachaReturnsFile(date) {
        await downloadReport(`/api/returns/nacha_files/wsfs?date=${encodeURIComponent(formatUSDateToISO(date))}`, 'WSFS Nacha');
    }

    // Not a file download — triggers report generation on P4.
    static async downloadFundingRequestReport(date) {
        const formattedDate = formatUSDateToISO(date);
        const response = await request('/api/reports/funding_requests', {
            method: 'POST',
            data: { fromDate: formattedDate, toDate: formattedDate }
        });
        return response;
    }

    // The CSV exports live on the same endpoints as their JSON listings; the
    // representation is chosen with the Accept header.
    static async downloadHoldsReport(date) {
        await downloadReport('/api/loans/held', 'Holds', true, 'text/csv');
    }

    static async downloadReturnsReport(date) {
        await downloadReport(`/api/returns?date=${encodeURIComponent(formatUSDateToISO(date))}`, 'Returns', true, 'text/csv');
    }

    static async downloadPaymentsReport(date, processingPaymentsOnly) {
        const formattedDate = encodeURIComponent(formatUSDateToISO(date));
        const endpoint = processingPaymentsOnly
            ? `/api/payments/processing?date=${formattedDate}`
            : `/api/payments?date=${formattedDate}`;
        const response = await request(endpoint, { responseType: 'blob', headers: { Accept: 'text/csv' } });

        if (isSuccessfulResponse(response)) {
            download(response.data, response.headers["x-filename"]);
            return response.data;
        } else if(response.status === 204) {
            return response.data;
        } else {
            throw new Error(`Server error downloading Payment file`);
        }
    }

    // TODO MIGRATION: loan-comment .txt file was assembled by DPP
    // (json2commentFile). P4 needs an endpoint returning the ready file
    // (see P4 task #4).
    static async downloadCommentsFile(loanNumber) {
        await downloadReport(`/api/loans/${loanNumber}/loan_comments/download`, 'Comments', false);
    }

    static async downloadNLSFundingXML(date) {
        await downloadReport(`/api/nls/funding_requests_file?date=${encodeURIComponent(formatUSDateToISO(date))}`, 'XML', false);
    }

    static async downloadNLSPaymentXML(date) {
        await downloadReport(`/api/nls/payments_file?date=${encodeURIComponent(formatUSDateToISO(date))}`, 'XML', false);
    }

    static async downloadNLSRefundXML(date) {
        await downloadReport(`/api/nls/refunds_file?date=${encodeURIComponent(formatUSDateToISO(date))}`, 'XML', false);
    }

    static async fetchNLSFundingXML(date) {
        const formattedDate = formatUSDateToISO(date);
        const response = await request(`/api/nls/funding_requests_file?date=${encodeURIComponent(formattedDate)}`, { responseType: 'text' });
        return response.data;
    }

    static async fetchNLSPaymentXML(date) {
        const formattedDate = formatUSDateToISO(date);
        const response = await request(`/api/nls/payments_file?date=${encodeURIComponent(formattedDate)}`, { responseType: 'text' });
        return response.data;
    }

    static async fetchNLSRefundXML(date) {
        const formattedDate = formatUSDateToISO(date);
        const response = await request(`/api/nls/refunds_file?date=${encodeURIComponent(formattedDate)}`, { responseType: 'text' });
        return response.data;
    }

    // TODO MIGRATION: PCG/delinquency-comment .txt file was assembled by DPP
    // (json2PCGCommentFile). P4 needs an endpoint returning the ready file.
    static async downloadPCGCommentsFile(loanNumber) {
        await downloadReport(`/api/loans/${loanNumber}/delinquency_comments/download`, 'PCG comments', false);
    }

    static async sendNLSFundingUpdate(xmlContent) {
        const response = await request.post('/api/nls/funding_update', { xml: xmlContent });
        return response;
    }

    static async sendNLSPaymentUpdate(xmlContent) {
        const response = await request.post('/api/nls/payment_update', { xml: xmlContent });
        return response;
    }

    static async sendNLSRefundUpdate(xmlContent) {
        const response = await request.post('/api/nls/refund_update', { xml: xmlContent });
        return response;
    }

    static async sendDailyProcessingNotifications(date) {
        const response = await request('/api/payments/dealer_notifications', {
            method: 'POST',
            data: { updateSystemSetting: true }
        });
        return response;
    }

    static async resendNotification(notificationId) {
        const response = await request(`/api/notifications/${notificationId}/resend`, {
            method: 'POST'
        });
        return response;
    }

    static async sendDailyFundingReport(date) {
        const response = await request('/api/notifications/send_internal_funding_email', { method: 'POST' });
        return response;
    }

    // DPP combined two system settings into one payload.
    static async getAutoSendStatus() {
        const [sendStatus, toggleAutoSend] = await Promise.all([
            request('/api/system_settings/sendStatus'),
            request('/api/system_settings/toggleAutoSend'),
        ]);
        return {
            sendStatus: sendStatus.data?.value,
            toggleAutoSend: toggleAutoSend.data?.value,
        };
    }

    static async getFundingNotificationStatus() {
        const response = await request('/api/system_settings/funding_notifications_sent');
        return { fundingNotificationsSent: response.data?.value };
    }

    static async setAutoSendStatus(status, toggle) {
        // DPP wrote two settings: sendStatus + toggleAutoSend.
        await request('/api/system_settings/sendStatus', {
            method: 'PATCH',
            data: { value: status }
        });
        const response = await request('/api/system_settings/toggleAutoSend', {
            method: 'PATCH',
            data: { value: toggle }
        });
        return response;
    }

    static async holdFundingRequests(loanNumbers) {
        const response = await request('/api/loans/holds', {
            method: 'POST',
            data: { loanNumbers: loanNumbers.flat(), holdType: 'funding' }
        });

        return response.data;
    }

    static async clearFundingRequests(loanNumbers) {
        const response = await request('/api/loans/holds', {
            method: 'DELETE',
            data: { loanNumbers: loanNumbers.flat() }
        });

        return response.data;
    }

    static async holdPayments(loanNumbers) {
        const response = await request('/api/loans/holds', {
            method: 'POST',
            data: { loanNumbers, holdType: 'payment' }
        });

        return response.data;
    }

    static async clearPayments(loanNumbers) {
        const response = await request('/api/loans/holds', {
            method: 'DELETE',
            data: { loanNumbers }
        });

        return response.data;
    }

    // DPP mapped the incoming hold objects to bare loan numbers before release.
    static async bulkReleaseHolds(loanNumbers) {
        const releaseList = loanNumbers.flat().map(h => h?.loanNumber ?? h);
        const response = await request('/api/loans/holds', {
            method: 'DELETE',
            data: { loanNumbers: releaseList }
        });

        return response.data;
    }

    static async bulkMarkPendingRelease(loanNumbers) {
        const response = await request('/api/loans/pending_releases', {
            method: 'POST',
            data: { loanNumbers }
        });

        return response.data;
    }

    static async bulkRemovePendingRelease(loanNumbers) {
        const response = await request('/api/loans/pending_releases', {
            method: 'DELETE',
            data: { loanNumbers }
        });

        return response.data;
    }

    static async markPaymentsAsProcessing(paymentIds) {
        const response = await request('/api/payments/processing', {
            method: 'POST',
            data: { paymentIds }
        });

        return response.data;
    }

    static async markLoansAsNotFunding(loanIds) {
        const response = await request('/api/loans/not_funding', {
            method: 'POST',
            data: { loanNumbers: loanIds }
        });

        return response.data;
    }

    static async markHoldsAsProcessing(holdsIds) {
        const response = await request('/api/payments/processing', {
            method: 'POST',
            data: { paymentIds: holdsIds }
        });

        return response.data;
    }

    static async pullFundingRequests(date) {
        const response = await request('/api/funding_requests/pull', {
            method: 'POST',
            data: {}
        });
        return response.data;
    }

    static async pullReturns(date) {
        const response = await request('/api/returns/imports', {
            method: 'POST',
            data: {}
        });
        return response.data;
    }

    // DPP reshaped the form payload: fundingDate -> requestDate, amount -> amountCents.
    static async newFundingRequest(fundingRequest){
        const response = await request('/api/funding_requests', {
            method: 'POST',
            data: {
                loanNumber: fundingRequest.loanNumber,
                requestDate: fundingRequest.fundingDate,
                amountCents: toCents(fundingRequest.amount),
            }
        });

        return response;
    }

    static async deleteFundingRequests(fundingRequestIds){
        const response = await request('/api/funding_requests/batch_destroy', {
            method: 'DELETE',
            data: { ids: fundingRequestIds }
        });

        return response.data;
    }

    // DPP deleted returns one-by-one; P4 has no batch endpoint.
    static async deleteReturns(returnIds){
        const results = [];
        for (const id of returnIds) {
            results.push(await request(`/api/returns/${id}`, { method: 'DELETE' }));
        }
        return results;
    }

    static async pullPayments(date) {
        const formattedDate = formatDate(date);
        const response = await request('/api/payments/imports', {
            method: 'POST',
            data: { date: formattedDate }
        });
        return response.data;
    }

    static async deletePayments(paymentIds) {
        const response = await request('/api/payments/inactivations', {
            method: 'POST',
            data: { paymentIds: [paymentIds].flat() }
        });

        return response.data;
    }

    static async newReturn(returns) {
        const response = await request(`/api/loans/${returns.loanNumber}/returns`, {
          method: 'POST',
          data: JSON.stringify(returns)
        });

        return response;
    }

    static async updatePayment(paymentId, loanNumber, data) {
        const response = await request(`/api/loans/${loanNumber}/payments/${paymentId}`, {
            method: 'PATCH',
            data: data
        });

        return response;
    }

    static async updateLoan(loanNumber, data) {
        const response = await request(`/api/loans/${loanNumber}`, {
            method: 'PUT',
            data: data
        });

        return response;
    }

    static async updateLoanNotes(loanNumber, notes) {
        return await LoanService.updateLoan(loanNumber, {notes});
    }

    static async updateFundingRequestProjectType(fundingRequestId, newValue) {
        // DPP forwarded this as a loan update keyed by the funding request id.
        const response = await request(`/api/loans/${fundingRequestId}`, {
            method: 'PUT',
            data: JSON.stringify({ projectType: newValue })
        });

        return response;
    }

    static async getHeldLoans() {
        const response = await request('/api/loans/held');

        return response;
    }

    static async getHeldLoanSummary() {
        const response = await request('/api/loans/holds/summary');

        return response;
    }

    static async getLoanNotifications(loanNumber) {
        // DPP issued this as a POST to P4.
        const response = await request(`/api/loans/${loanNumber}/notifications`, {
            method: 'POST'
        });

        return response;
    }

    static async newLoanComment(loanNumber, comment, category) {
        const response = await request(`/api/loans/${loanNumber}/loan_comments`, {
            method: 'POST',
            data: JSON.stringify({ comment, category })
        });

        return response;
    }

    static async deleteLoanComment(id, loanNumber) {
        const response = await request(`/api/loans/${loanNumber}/loan_comments/${id}/inactivate`, {
            method: 'DELETE'
        });

        return response;
    }

    static async getLoanCommentsForLoan(loanNumber) {
        const response = await request(`/api/loans/${loanNumber}/loan_comments`);
        // DPP wrapped P4's array as { comments: [...] }.
        return { comments: response.data };
    }

    static async getLoanPopupsForLoan(loanNumber) {
        const response = await request(`/api/loans/${loanNumber}/loan_comments/popup_comments`);

        return { comments: response.data };
    }

    static async getLoanDelinquencyCommentsForLoan(loanNumber) {
        const response = await request(`/api/loans/${loanNumber}/delinquency_comments`);

        return { comments: response.data?.delinquencyComments ?? [] };
    }
    static async getNLSCommentsForLoan(loanNumber) {
        const response = await request(`/api/loans/${loanNumber}/nls_comments`);

        return response;
    }

    static async getLoanMilestones(loanNumber) {
        const response = await request(`/api/loans/${loanNumber}/milestones`);
        return response;
    }

    static async executeFundingRules(date) {
        const response = await request('/api/rules/execute', {
            method: 'POST',
            data: {}
        });

        return response.data;
    }

    static async markPendingRelease(loanNumber) {
        const response = await request(`/api/loans/${loanNumber}`, {
            method: 'PUT',
            data: {
                holdStatus: LOAN_HOLD_STATUS.PENDING_RELEASE,
                holdUpdated: new Date().toISOString(),
            }
        });

        return response;
    }

    static async removePendingRelease(loanNumber) {
        const response = await request(`/api/loans/${loanNumber}`, {
            method: 'PUT',
            data: {
                holdStatus: LOAN_HOLD_STATUS.HOLD_FOR_FUNDING,
                holdUpdated: new Date().toISOString(),
            }
        });

        return response;
    }

    static async downloadNotificationPreview(date) {
        const formattedDate = new Date(date).toISOString().slice(0, 10);
        const response = await request(`/api/payments/notification_previews?date=${encodeURIComponent(formattedDate)}`, { responseType: 'blob' });
        if (isSuccessfulResponse(response)) {
            download(response.data, response.headers["x-filename"]);
            return response.data;
        } else {
            throw new Error("Server error downloading notification preview");
        }
    }

    static async downloadNLSHoldRelease() {
        const response = await request('/api/nls/release_holds_file?user=system-automation', { responseType: 'blob' });
        if (isSuccessfulResponse(response)) {
            download(response.data, response.headers["x-filename"]);
            return response.data;
        } else {
            throw new Error("Server error downloading NLS Hold Release XML");
        }
    }

    static async getDeletedFRPR(loanNumber){
        const response = await request(`/api/loans/${loanNumber}/inactive_transactions`);
        if (isSuccessfulResponse(response)) {
            console.log(response.data.data);
            return response;
        } else {
            return false;
        }
    }
}
